/**
 * Logger Utility with Environment-Based Feature Flag and LocalStorage Override
 *
 * This logger is disabled by default in production environments to reduce
 * noise and improve performance. It is enabled in development (localhost).
 *
 * You can enable debug logging in production by setting a localStorage flag:
 *   localStorage.setItem('debug', 'true');
 *   // Then reload the page
 *
 * To disable debug logging in production:
 *   localStorage.removeItem('debug');
 *   // or
 *   localStorage.setItem('debug', 'false');
 *   // Then reload the page
 *
 * Usage:
 *   import logger from '@/utils/logger';
 *
 *   logger.log('This is a log message');
 *   logger.warn('This is a warning');
 *   logger.error('This is an error');
 *   logger.debug('This is debug info');
 */

const isProduction = process.env.NODE_ENV === 'production';
const isDevelopment = !isProduction;

/**
 * Check if debug mode is enabled via localStorage
 * @returns {boolean} - Whether debug mode is enabled
 */
const isDebugEnabled = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }

  try {
    const debugFlag = localStorage.getItem('debug');
    return debugFlag === 'true' || debugFlag === '1';
  } catch (e) {
    // localStorage might be blocked or unavailable
    return false;
  }
};

// Feature flag: logging is enabled in development, or when debug flag is set in localStorage
const LOGGING_ENABLED = isDevelopment || isDebugEnabled();

/**
 * Logger class with environment-aware feature flag and localStorage override
 */
class Logger {
  constructor() {
    // Log a message when debug mode is enabled in production
    if (isProduction && isDebugEnabled()) {
      console.log(
        '%c[Logger] Debug mode enabled via localStorage',
        'color: #00ff00; font-weight: bold;'
      );
    }
  }
  /**
   * Log general information
   * @param {...any} args - Arguments to log
   */
  log(...args) {
    if (LOGGING_ENABLED) {
      console.log(...args);
    }
  }

  /**
   * Log warnings
   * @param {...any} args - Arguments to log
   */
  warn(...args) {
    if (LOGGING_ENABLED) {
      console.warn(...args);
    }
  }

  /**
   * Log errors (always enabled, even in production)
   * @param {...any} args - Arguments to log
   */
  error(...args) {
    // Errors are always logged, even in production
    console.error(...args);
  }

  /**
   * Log debug information
   * @param {...any} args - Arguments to log
   */
  debug(...args) {
    if (LOGGING_ENABLED) {
      console.debug(...args);
    }
  }

  /**
   * Log informational messages
   * @param {...any} args - Arguments to log
   */
  info(...args) {
    if (LOGGING_ENABLED) {
      console.info(...args);
    }
  }

  /**
   * Check if logging is enabled
   * @returns {boolean} - Whether logging is enabled
   */
  isEnabled() {
    return LOGGING_ENABLED;
  }

  /**
   * Enable debug mode (sets localStorage flag)
   * Call this from browser console to enable logging in production
   */
  enableDebug() {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('debug', 'true');
        console.log(
          '%c[Logger] Debug mode enabled! Reload the page to activate logging.',
          'color: #00ff00; font-weight: bold;'
        );
      } catch (e) {
        console.error('[Logger] Failed to enable debug mode:', e);
      }
    }
  }

  /**
   * Disable debug mode (removes localStorage flag)
   * Call this from browser console to disable logging in production
   */
  disableDebug() {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem('debug');
        console.log(
          '%c[Logger] Debug mode disabled! Reload the page to deactivate logging.',
          'color: #ff6600; font-weight: bold;'
        );
      } catch (e) {
        console.error('[Logger] Failed to disable debug mode:', e);
      }
    }
  }

  /**
   * Check if debug mode is currently active
   * @returns {boolean} - Whether debug mode is active
   */
  isDebugMode() {
    return isDebugEnabled();
  }
}

// Export a singleton instance
const logger = new Logger();

// Make logger available globally in browser for easy debugging
if (typeof window !== 'undefined') {
  window.logger = logger;
}

export default logger;
